import { IComparable } from "../../../Global/Base/IComparable";
import { Localization } from "../Localization";
import { EnumDescription, IEnum, IEnumDescriptionWithDataType, IEnumType } from "./IEnums";

export class Enum implements IEnum {
    private readonly _type: IEnumType;
    protected _ed: EnumDescription;

    constructor(description: EnumDescription, type: IEnumType) {
        this._ed = description;
        this._type = type;
    }

    public getType(): IEnumType {
        return this._type;
    }

    public getTypeName(): string {
        return this._type.type;
    }

    public toString(): string {
        return this._ed.Name;
    }

    public toLocaleString(): string {
        return this._ed.Description;
    }

    public getLocalizedName(): Promise<string> {
        return Localization.getTextPromise(this._ed.LocKey);
    }

    public getName(): string {
        return this._ed.Name;
    }

    public valueOf(): number {
        return this._ed.IntValue;
    }

    public toJSON(): number {
        return this._ed.IntValue;
    }

    public [Symbol.toPrimitive](hint: "default" | "string" | "number"): string | number {
        switch (hint) {
            case "number": // nastane napriklad u  Number(v), v * 1, v - 1, v < 0 ale i v < "abc"
                return this._ed.IntValue;
            case "string": // nastane napriklad u String(v), `abc ${v} sws`, v in {}
                return this._ed.Name;
            case "default": // nastane napriklad u v + 1 a v == 0  - coz je trosku blbe :-(
                return this._ed.IntValue;
            default:
                throw new TypeError();
        }
    }

    private static _methods = ["getType", "toJSON", "valueOf", "getName", "getLocalizedName", "toLocaleString", "getTypeName" ];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._methods.some(value => typeof instance[value] !== "function");
    }
}

/**
 * Enum rozšířený o možnost získat enumdescription.datatype
 */
export class EnumWithDataType extends Enum {
    protected override _ed: IEnumDescriptionWithDataType;

    constructor(description: IEnumDescriptionWithDataType, type: IEnumType) {
        super(description, type);
    }

    /**
     * Vrátí DataType
     */
    public getDataType(): string {
        return (this._ed).DataType;
    }

    public override getLocalizedName(): Promise<string> {
        return Localization.getTextPromiseWithParams(this._ed.LocKey, this._ed.LocParams);
    }
}

export class EnumType implements IEnumType, IComparable {
    constructor(public descriptions: EnumDescription[], public type: string) {
        descriptions.forEach((e) => {
            const value = e instanceof IEnumDescriptionWithDataType ? new EnumWithDataType(e, this) : new Enum(e, this);
            this._values.set(e.Name, value);
            this._values.set(e.IntValue, value);
        });
    }

    // predelano na vnitrni struktury aby nahodou nedochazelo ke kolizi nazvu v Enum hodotach a nazvy v EnumType
    private _values: Map<string | number, IEnum> = new Map<string | number, Enum>();

    public getValue(value: string | number): IEnum | undefined {
        if (!this._values.has(value))
            if (Number.isNaN(value = +value))
                return undefined;
        return this._values.get(value);
    }

    public hasValue(value: string | number): boolean {
        return this._values.has(value);
    }

    public *getItemNames(): IterableIterator<string> {
        for (const item of this._values.keys())
            if (typeof item === "string")
                yield item;
    }

    public compare(value: any): boolean {
        if (value instanceof EnumType) {
            if (this.type !== value.type) return false;
            if (this._values.size !== value._values.size) return false;

            for (const [key, val] of this._values) {
                if (!value._values.has(key)) return false;
                if (val.valueOf() !== value._values.get(key).valueOf()) return false;
            }

            return true;
        }
        return false;
    }
}

export const EnumProxyHandler: ProxyHandler<EnumType> = {
    get: function(target, property) {
        return target.getValue(property as any);
    },
    has: function(target, property) {
        return target.hasValue(property as any);
    },
    ownKeys: function(target) {
        const result: Array<string> = [];
        for (const item of target.getItemNames())
            result.push(item);
        return result;
    },
    getOwnPropertyDescriptor: function(target, property) {
        if (!target.hasValue(property as any))
            return undefined;
        return { configurable: true, enumerable: true, writable: false, value: target.getValue(property as any) };
    },
};
